import React, { useState, useCallback, useEffect } from 'react';
import { Card, Alert, Spinner } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';

import GenericImporterImportTypes from './GenericImporterTypes';
import GenericImporterStepperProgressTracker from './GenericImporterStepProgressTracker';
import GenericImporterFileUploadStep from './GenericImporterFileUploadStep';
import GenericImporterColumnMappingStep from './GenericImporterColumnMappingStep';
import GenericImporterDataPreviewStep from './GenericImporterDataPreviewStep';

import { getApiUrl } from '../../../utils/ApiUtils';
import axios from '../../../services/AxiosInstance';

// Step constants
const STEPS = {
  FILE_UPLOAD: 'file_upload',
  COLUMN_MAPPING: 'column_mapping',
  PREVIEW_IMPORT: 'preview_import'
};

const GenericImporter = () => {
  // State for step management
  const [activeStep, setActiveStep] = useState(STEPS.FILE_UPLOAD);
  const [stepOperationInProgress, setStepOperationInProgress] = useState(false);

  // State for data management
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [columnMappings, setColumnMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Validation states
  const [columnMappingIsValid, setColumnMappingIsValid] = useState(false);
  const [previewIsValid, setPreviewIsValid] = useState(false);
  const [dataToImport, setDataToImport] = useState(null);

  const { companyId, importType } = useParams();
  const configKey = importType?.toUpperCase();
  const config = GenericImporterImportTypes[configKey];
  const history = useHistory();

  // Handle file upload and parsing
  const handleFile = async (file, data, fileHeaders) => {
    setLoading(true);
    setError(null);
    try {
      setUploadedFile(file);
      setPreviewData(data);
      setHeaders(fileHeaders);
      setActiveStep(STEPS.COLUMN_MAPPING);
    } catch (err) {
      setError('Det gick inte att läsa filen: ' + err.message);
      Swal.fire({
        icon: 'error',
        title: 'Fel',
        text: 'Det gick inte att läsa filen: ' + err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle column mapping
  const handleColumnMapping = (systemField, selectedColumn) => {
    setColumnMappings(prev => ({
      ...prev,
      [systemField]: selectedColumn
    }));
  };

  // Navigate to next step
  const handleNextStep = () => {
    if (activeStep === STEPS.FILE_UPLOAD) {
      setActiveStep(STEPS.COLUMN_MAPPING);
    } else if (activeStep === STEPS.COLUMN_MAPPING) {
      setActiveStep(STEPS.PREVIEW_IMPORT);
    }
  };

  // Navigate to previous step
  const handlePreviousStep = () => {
    if (activeStep === STEPS.COLUMN_MAPPING) {
      setActiveStep(STEPS.FILE_UPLOAD);
    } else if (activeStep === STEPS.PREVIEW_IMPORT) {
      setActiveStep(STEPS.COLUMN_MAPPING);
    }
  };

  // Handle final import
  const handleImport = async () => {
    if (!dataToImport || !previewIsValid) {
      return;
    }

    setStepOperationInProgress(true);
    try {
      const formData = new FormData();
      formData.append('mappings', JSON.stringify(columnMappings));
      formData.append('data', JSON.stringify(dataToImport));

      const importUrl = getApiUrl(`${companyId}${config.apiEndpoint}`);
      const response = await axios.post(importUrl, formData, { withCredentials: true });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Framgång',
          text: response.data.message,
          confirmButtonText: 'OK'
        }).then(() => {
          history.push(`/${companyId}/${config.type}`);
        });
      }

      return response.data;
    } catch (error) {
      console.error('Import error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Import misslyckades',
        text: error.response?.data?.message || 'Ett fel uppstod under importen'
      });
    } finally {
      setStepOperationInProgress(false);
    }
  };

  if (!config) {
    return (
      <Card>
        <Card.Header className="d-flex align-items-center gap-2">
          <AlertTriangle size={20} className="text-danger" />
          <h4 className="mb-0">Ogiltigt importformat</h4>
        </Card.Header>
        <Card.Body className="text-center">
          <p>Det angivna importformatet stöds inte.</p>
          <button
            className="btn btn-primary"
            onClick={() => history.goBack()}
          >
            <ArrowLeft className="me-2" size={16} />
            Gå tillbaka
          </button>
        </Card.Body>
      </Card>
    );
  }

  if (loading && !previewData && !headers.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
        <p className="mt-3">Bearbetar fil...</p>
      </div>
    );
  }

  const IconComponent = config.icon;

  return (
    <>
      <style>{`
        /* We'll add all the CSS styles here */
        .importer-container {
          margin-bottom: 2rem;
        }

        .importer-header {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          color: #1e293b;
        }
        
        .importer-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #1e293b;
        }

        .importer-body {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .step-content-container {
          padding: 2rem;
        }

        /* Step navigation styles */
        .step-navigation-bar {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .step-nav-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .step-nav-current {
          font-weight: 600;
          color: #1e293b;
          font-size: 1rem;
        }
        
        .step-nav-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        
        .step-nav-btn {
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          min-width: 120px;
          justify-content: center;
        }
        
        .btn-nav-previous {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-nav-previous:hover:not(:disabled) {
          background: #f1f5f9;
          color: #475569;
        }
        
        .btn-nav-next {
          background: #3b82f6;
          color: white;
        }
        
        .btn-nav-next:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .btn-nav-import {
          background: #44814E;
          color: white;
        }
        
        .btn-nav-import:hover:not(:disabled) {
          background: #36673e;
          transform: translateY(-1px);
        }
        
        .btn-nav-import:disabled, .btn-nav-next:disabled, .btn-nav-previous:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }
        
        .step-nav-loading {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .step-navigation-bar {
            flex-direction: column;
            gap: 1rem;
            padding: 1rem;
          }
          
          .step-nav-actions {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>

      <div className="importer-container">
        {/* Header with icon and title */}
        <div className="importer-header">
          <div className="d-flex justify-content-between align-items-start">
            <div style={{ flex: 1 }}>
              <h1 className="importer-title">
                <IconComponent size={24} color="#3b82f6" />
                {config.title}
              </h1>
              <p className="text-muted">
                {config.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <GenericImporterStepperProgressTracker
          activeStep={activeStep}
          config={config}
          onStepClick={(step) => {
            // Only allow clicking on completed steps
            if ((activeStep === STEPS.PREVIEW_IMPORT && step === STEPS.COLUMN_MAPPING) ||
              ((activeStep === STEPS.PREVIEW_IMPORT || activeStep === STEPS.COLUMN_MAPPING) &&
                step === STEPS.FILE_UPLOAD)) {
              setActiveStep(step);
            }
          }}
        />

        {/* Main content container */}
        <div className="importer-body">
          <div className="step-content-container">
            {activeStep === STEPS.FILE_UPLOAD && (
              <GenericImporterFileUploadStep
                config={config}
                onFileSelected={handleFile}
                loading={loading}
              />
            )}

            {activeStep === STEPS.COLUMN_MAPPING && (
              <GenericImporterColumnMappingStep
                config={config}
                headers={headers}
                previewData={previewData}
                columnMappings={columnMappings}
                onColumnMappingChange={handleColumnMapping}
                loading={loading}
                onValidationChange={setColumnMappingIsValid}
              />
            )}

            {activeStep === STEPS.PREVIEW_IMPORT && (
              <GenericImporterDataPreviewStep
                config={config}
                previewData={previewData}
                columnMappings={columnMappings}
                loading={stepOperationInProgress}
                onValidationChange={setPreviewIsValid}
                onDataPrepared={setDataToImport}
              />
            )}
          </div>

          {/* Step Navigation */}
          <div className="step-navigation-bar">
            <div className="step-nav-info">
              <div className="step-nav-current">
                Steg {activeStep === STEPS.FILE_UPLOAD ? '1' :
                  activeStep === STEPS.COLUMN_MAPPING ? '2' : '3'} av 3
              </div>
            </div>

            <div className="step-nav-actions">
              {activeStep !== STEPS.FILE_UPLOAD && (
                <button
                  className="step-nav-btn btn-nav-previous"
                  onClick={handlePreviousStep}
                  disabled={loading || stepOperationInProgress}
                >
                  <ArrowLeft size={16} />
                  Föregående
                </button>
              )}

              {activeStep !== STEPS.PREVIEW_IMPORT ? (
                <button
                  className="step-nav-btn btn-nav-next"
                  onClick={handleNextStep}
                  disabled={
                    (activeStep === STEPS.FILE_UPLOAD && !uploadedFile) ||
                    (activeStep === STEPS.COLUMN_MAPPING && !columnMappingIsValid) ||
                    loading ||
                    stepOperationInProgress
                  }
                >
                  Nästa steg
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  className="step-nav-btn btn-nav-import"
                  onClick={handleImport}
                  disabled={!previewIsValid || !dataToImport || dataToImport.length === 0 || stepOperationInProgress}
                >
                  {stepOperationInProgress ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Importerar...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Importera {dataToImport?.length || 0} {dataToImport?.length === 1 ? 'rad' : 'rader'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenericImporter;